const websocketUrl = process.env.REACT_APP_WEBSOCKET_URL || 'wss://api.lk-impulse.ru:9090';

/** Таймаут установки соединения (мс). */
const CONNECT_TIMEOUT_MS = 30000;

/** Сколько ждать без OPEN после обрыва, прежде чем считать WS недоступным (мс). */
const UNAVAILABLE_AFTER_MS = 60000;

/** Задержка перед авто-реконнектом (мс). */
const RECONNECT_DELAY_MS = 6000;

/** Логи lifecycle (creating/open/close). Выкл. только явно: REACT_APP_WEBSOCKET_DEBUG=false */
const WS_DEBUG = process.env.REACT_APP_WEBSOCKET_DEBUG !== 'false';

/**
 * @typedef {'connecting' | 'open' | 'reconnecting' | 'unavailable'} WebSocketConnectionStatus
 */

/**
 * @typedef {Object} WebSocketConnectionStatusPayload
 * @property {WebSocketConnectionStatus} status
 * @property {'connect_timeout' | 'disconnected' | 'error'} [reason]
 */

const getReadyStateLabel = (readyState) => {
    switch (readyState) {
        case WebSocket.CONNECTING:
            return 'CONNECTING';
        case WebSocket.OPEN:
            return 'OPEN';
        case WebSocket.CLOSING:
            return 'CLOSING';
        case WebSocket.CLOSED:
            return 'CLOSED';
        default:
            return String(readyState);
    }
};

class WebSocketService {
    constructor() {
        this.socket = null;
        this.socketCounter = 0;
        this.activeConnectionId = null;
        this.pendingConnect = false;
        this.reconnectTimeout = null;
        this.connectTimeoutId = null;
        this.unavailableAlertTimer = null;
        this.unavailableAlertScheduled = false;
        this.manualClose = false;
        this.openCallbacks = [];
        /** @type {Array<(payload: WebSocketConnectionStatusPayload) => void>} */
        this.connectionStatusCallbacks = [];
        this.callbacks = {
            notifications: [],
            notification: [],
            chat_message: [],
            chat_message_reaction: [],
            file_uploaded: [],
            chat_message_deleted: [],
            chat_topic_changed: [],
            chat_support_status_changed: [],
            chat_support_ticket_changed: [],
            'chat.read': [],
            active_users: [],
            chat_notifications: [],
            test_sockets: [],
            vk_message_inserted: [],
            vk_message_read: [],
            vk_message_delivered: [],
            vk_message_not_delivered: [],
            integration_message_inserted: [],
            integration_message_read: [],
            integration_message_delivered: [],
            integration_message_not_delivered: [],
            lead_created: [],
            lead_duplicate_received: [],
            task_tracker_task_changed: [],
            ai_tutor_message: [],
            'ai_tutor.message.failed': [],
            code_error_ai_explanation: [],
            video_compress_progress: [],
            student_excel_import_progress: []
        };
    }

    log(connectionId, ...args) {
        if (!WS_DEBUG) {
            return;
        }

        console.log('[WS]', connectionId ?? '-', ...args);
    }

    isOpen() {
        return this.socket?.readyState === WebSocket.OPEN;
    }

    isSocketBusy(socket = this.socket) {
        if (!socket) {
            return false;
        }

        const { readyState } = socket;

        return (
            readyState === WebSocket.OPEN ||
            readyState === WebSocket.CONNECTING ||
            readyState === WebSocket.CLOSING
        );
    }

    /** Для дебага: снимок состояния клиента (в консоли: `window.__wsDebug()`). */
    getDebugSnapshot() {
        return {
            url: websocketUrl,
            activeConnectionId: this.activeConnectionId,
            manualClose: this.manualClose,
            pendingConnect: this.pendingConnect,
            hasReconnectTimer: Boolean(this.reconnectTimeout),
            hasConnectTimeout: Boolean(this.connectTimeoutId),
            socketReadyState: this.socket
                ? getReadyStateLabel(this.socket.readyState)
                : null,
            isOpen: this.isOpen(),
            subscribers: Object.fromEntries(
                Object.entries(this.callbacks).map(([key, handlers]) => [
                    key,
                    handlers.length,
                ]),
            ),
        };
    }

    /** @param {WebSocketConnectionStatusPayload} payload */
    emitConnectionStatus(payload) {
        this.connectionStatusCallbacks.forEach((callback) => {
            try {
                callback(payload);
            } catch (error) {
                console.error('WebSocket connection status callback error:', error);
            }
        });
    }

    clearConnectTimeout() {
        if (this.connectTimeoutId) {
            clearTimeout(this.connectTimeoutId);
            this.connectTimeoutId = null;
        }
    }

    clearReconnectTimeout() {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
    }

    clearUnavailableAlertTimer() {
        if (this.unavailableAlertTimer) {
            clearTimeout(this.unavailableAlertTimer);
            this.unavailableAlertTimer = null;
        }
        this.unavailableAlertScheduled = false;
    }

    scheduleUnavailableAlert(reason = 'disconnected') {
        if (this.unavailableAlertScheduled || this.manualClose) {
            return;
        }

        this.unavailableAlertScheduled = true;
        this.unavailableAlertTimer = setTimeout(() => {
            this.unavailableAlertTimer = null;
            this.unavailableAlertScheduled = false;

            if (this.manualClose || this.isOpen()) {
                return;
            }

            this.emitConnectionStatus({ status: 'unavailable', reason });
        }, UNAVAILABLE_AFTER_MS);
    }

    scheduleReconnect(connectionId, delay = RECONNECT_DELAY_MS) {
        if (this.manualClose || this.reconnectTimeout) {
            return;
        }

        this.log(connectionId, 'reconnect scheduled', `${delay}ms`);

        this.reconnectTimeout = setTimeout(() => {
            this.reconnectTimeout = null;
            this.connect('reconnect');
        }, delay);
    }

    handleSocketClose(connectionId, socket, event) {
        if (this.socket !== socket) {
            this.log(
                connectionId,
                'close ignored',
                'stale socket',
                event?.code,
                event?.reason,
                getReadyStateLabel(socket.readyState),
            );
            return;
        }

        this.log(
            connectionId,
            'close',
            event?.code,
            event?.reason,
            getReadyStateLabel(socket.readyState),
        );

        this.socket = null;
        this.activeConnectionId = null;
        this.clearConnectTimeout();

        if (this.manualClose) {
            this.pendingConnect = false;
            return;
        }

        this.emitConnectionStatus({ status: 'reconnecting', reason: 'disconnected' });
        this.scheduleUnavailableAlert('disconnected');

        if (this.pendingConnect) {
            this.pendingConnect = false;
            this.scheduleReconnect(connectionId, 0);
            return;
        }

        this.scheduleReconnect(connectionId);
    }

    createSocket(connectionId) {
        const socket = new WebSocket(websocketUrl);

        this.socket = socket;
        this.activeConnectionId = connectionId;
        this.log(connectionId, 'creating', websocketUrl);

        this.connectTimeoutId = setTimeout(() => {
            this.connectTimeoutId = null;

            if (this.socket !== socket || socket.readyState !== WebSocket.CONNECTING) {
                this.log(
                    connectionId,
                    'connect timeout ignored',
                    getReadyStateLabel(socket.readyState),
                );
                return;
            }

            this.log(connectionId, 'connect timeout', 'closing CONNECTING socket');

            try {
                socket.close();
            } catch (error) {
                console.error('[WS]', connectionId, 'close after timeout failed', error);
            }

            this.scheduleUnavailableAlert('connect_timeout');
        }, CONNECT_TIMEOUT_MS);

        socket.onopen = () => {
            if (this.socket !== socket) {
                this.log(connectionId, 'open ignored', 'stale socket');
                return;
            }

            this.log(connectionId, 'open');
            this.clearReconnectTimeout();
            this.clearConnectTimeout();
            this.clearUnavailableAlertTimer();
            this.pendingConnect = false;
            this.emitConnectionStatus({ status: 'open' });

            const profile = localStorage.getItem('profile');
            if (profile) {
                const userId = JSON.parse(profile).id;
                // Чаты уроков доступны только соединению с подтверждённой identity,
                // поэтому вместе с userId отправляем JWT: user_id бэкенд берёт из него.
                const token = localStorage.getItem('userToken') || undefined;
                this.send('register', { userId, token, origin: window.location.origin });
            }

            this.openCallbacks.forEach((callback) => callback());
        };

        socket.onmessage = (event) => {
            if (this.socket !== socket) {
                return;
            }

            const message = JSON.parse(event.data);
            if (this.callbacks[message.type]) {
                this.callbacks[message.type].forEach((callback) => callback(message));
            }
        };

        socket.onclose = (event) => {
            this.handleSocketClose(connectionId, socket, event);
        };

        socket.onerror = (error) => {
            if (this.socket !== socket) {
                this.log(connectionId, 'error ignored', 'stale socket', error);
                return;
            }

            this.log(connectionId, 'error', error);
            this.emitConnectionStatus({ status: 'reconnecting', reason: 'error' });
            this.scheduleUnavailableAlert('error');
        };
    }

    /** @param {(payload: WebSocketConnectionStatusPayload) => void} callback */
    subscribeConnectionStatus(callback) {
        this.connectionStatusCallbacks.push(callback);

        if (this.isOpen()) {
            callback({ status: 'open' });
        } else if (this.socket?.readyState === WebSocket.CONNECTING) {
            callback({ status: 'connecting' });
        }

        return () => {
            this.connectionStatusCallbacks = this.connectionStatusCallbacks.filter(
                (item) => item !== callback,
            );
        };
    }

    connect(reason = 'manual') {
        if (this.isSocketBusy()) {
            if (this.socket?.readyState === WebSocket.CLOSING) {
                this.pendingConnect = true;
                this.log(
                    this.activeConnectionId,
                    'connect deferred',
                    reason,
                    'socket CLOSING',
                );
            } else {
                this.log(
                    this.activeConnectionId,
                    'connect skipped',
                    reason,
                    getReadyStateLabel(this.socket.readyState),
                );
            }
            return;
        }

        try {
            this.manualClose = false;
            this.pendingConnect = false;
            this.clearConnectTimeout();
            this.clearReconnectTimeout();
            this.emitConnectionStatus({ status: 'connecting' });

            const connectionId = ++this.socketCounter;
            this.createSocket(connectionId);
        } catch (error) {
            console.error('[WS] connect failed', error);
            this.clearConnectTimeout();
            this.scheduleUnavailableAlert('error');
        }
    }

    subscribe(type, callback) {
        try {
            if (this.callbacks[type]) {
                this.callbacks[type].push(callback);
                return () => {
                    this.callbacks[type] = this.callbacks[type].filter((item) => item !== callback);
                };
            }

            console.warn(`Unknown callback type: ${type}`);
        } catch (error) {
            console.error('Error subscribing to WebSocket event:', error);
        }

        return undefined;
    }

    subscribeOpen(callback) {
        this.openCallbacks.push(callback);

        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            callback();
        }

        return () => {
            this.openCallbacks = this.openCallbacks.filter((item) => item !== callback);
        };
    }

    send(type, data) {
        try {
            if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                this.socket.send(JSON.stringify({ type, ...data }));
            } else {
                console.error('WebSocket is not open');
            }
        } catch (error) {
            console.error('Error sending WebSocket message:', error);
        }
    }

    disconnect() {
        try {
            this.manualClose = true;
            this.pendingConnect = false;
            this.clearReconnectTimeout();
            this.clearConnectTimeout();
            this.clearUnavailableAlertTimer();

            if (this.socket) {
                const socket = this.socket;
                const connectionId = this.activeConnectionId;

                socket.onopen = null;
                socket.onmessage = null;
                socket.onerror = null;
                socket.onclose = null;

                this.socket = null;
                this.activeConnectionId = null;

                try {
                    socket.close();
                } catch (error) {
                    console.error('[WS]', connectionId, 'disconnect close failed', error);
                }

                this.log(connectionId, 'disconnect');
            }

            this.clearCallbacks();
            this.openCallbacks = [];
        } catch (error) {
            console.error('Error disconnecting WebSocket:', error);
        }
    }

    clearCallbacks() {
        Object.keys(this.callbacks).forEach((key) => {
            this.callbacks[key] = [];
        });
    }
}

const websocketService = new WebSocketService();

if (typeof window !== 'undefined') {
    window.__wsDebug = () => websocketService.getDebugSnapshot();
}

export default websocketService;
